import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { Container, Row, Col, Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { promptsAPI, tagsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Save, X, Plus, Tag, Eye, Users, Lock } from 'lucide-react';
import TemplateVariables from '../components/TemplateVariables';
import PromptPreview from '../components/PromptPreview';

const CreatePromptPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    description: '',
    visibility: user?.preferences?.defaultVisibility || 'private',
    category: 'general',
    aiModels: [],
    isTemplate: false,
    templateVariables: []
  });
  
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch available tags
  const { data: tagsData, isLoading: tagsLoading } = useQuery(
    ['tags', 'all'],
    () => tagsAPI.getTags({ limit: 100 }),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch popular tags for suggestions
  const { data: popularTags } = useQuery(
    ['tags', 'popular'],
    () => tagsAPI.getPopularTags(10),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Create prompt mutation
  const createPromptMutation = useMutation(
    (data) => promptsAPI.createPrompt(data),
    {
      onSuccess: (response) => {
        toast.success('Prompt erfolgreich erstellt!');
        navigate(`/prompts/${response.data.id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fehler beim Erstellen des Prompts');
        if (error.response?.data?.details) {
          setErrors(error.response.data.details.reduce((acc, detail) => {
            acc[detail.path] = detail.msg;
            return acc;
          }, {}));
        }
      }
    }
  );

  // Create tag mutation
  const createTagMutation = useMutation(
    (tagData) => tagsAPI.createTag(tagData),
    {
      onSuccess: (response) => {
        setSelectedTags([...selectedTags, response.data]);
        setNewTag('');
        toast.success('Tag erfolgreich erstellt!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fehler beim Erstellen des Tags');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAIModelChange = (model) => {
    setFormData(prev => ({
      ...prev,
      aiModels: prev.aiModels.includes(model)
        ? prev.aiModels.filter(m => m !== model)
        : [...prev.aiModels, model]
    }));
  };

  const handleTagSelect = (tag) => {
    if (!selectedTags.find(t => t.id === tag.id)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleTagRemove = (tagId) => {
    setSelectedTags(selectedTags.filter(tag => tag.id !== tagId));
  };

  const handleCreateTag = () => {
    if (newTag.trim()) {
      createTagMutation.mutate({
        name: newTag.trim().toLowerCase().replace(/\s+/g, '-'),
        displayName: newTag.trim(),
        color: '#6c757d'
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Titel ist erforderlich';
    if (!formData.content.trim()) newErrors.content = 'Inhalt ist erforderlich';
    if (formData.title.length > 200) newErrors.title = 'Titel muss weniger als 200 Zeichen haben';
    if (formData.content.length > 10000) newErrors.content = 'Inhalt muss weniger als 10000 Zeichen haben';
    if (formData.description.length > 500) newErrors.description = 'Beschreibung muss weniger als 500 Zeichen haben';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const promptData = {
      ...formData,
      tags: selectedTags.map(tag => tag.id)
    };

    createPromptMutation.mutate(promptData);
  };

  const aiModelOptions = [
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: 'claude-3', label: 'Claude-3' },
    { value: 'claude-2', label: 'Claude-2' },
    { value: 'bard', label: 'Bard' },
    { value: 'other', label: 'Other' }
  ];

  const categoryOptions = [
    { value: 'general', label: 'General' },
    { value: 'writing', label: 'Writing' },
    { value: 'coding', label: 'Coding' },
    { value: 'analysis', label: 'Analysis' },
    { value: 'creative', label: 'Creative' },
    { value: 'business', label: 'Business' },
    { value: 'education', label: 'Education' }
  ];

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-1">Neues Prompt erstellen</h1>
              <p className="text-muted mb-0">Build and organize your AI prompts</p>
            </div>
            <Button variant="outline-secondary" onClick={() => navigate('/prompts')}>
              Abbrechen
            </Button>
          </div>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={8}>
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Prompt-Details</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Title */}
                    <Form.Group className="mb-3">
                      <Form.Label>Titel *</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Gib einen beschreibenden Titel für dein Prompt ein"
                        isInvalid={!!errors.title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.title}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {formData.title.length}/200 Zeichen
                      </Form.Text>
                    </Form.Group>

                    {/* Description */}
                    <Form.Group className="mb-3">
                      <Form.Label>Beschreibung</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Gib eine kurze Beschreibung für dein Prompt ein"
                        isInvalid={!!errors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {formData.description.length}/500 Zeichen
                      </Form.Text>
                    </Form.Group>

                    {/* Content */}
                    <Form.Group className="mb-3">
                      <Form.Label>Prompt-Inhalt *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={12}
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        placeholder="Gib deinen KI-Prompt hier ein..."
                        className="font-monospace"
                        isInvalid={!!errors.content}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.content}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {formData.content.length}/10000 Zeichen
                      </Form.Text>
                    </Form.Group>

                    {/* Template Variables */}
                    {formData.isTemplate && (
                      <Form.Group className="mb-3">
                        <Form.Label>Template Variables</Form.Label>
                        <TemplateVariables
                          variables={formData.templateVariables}
                          onChange={(variables) => setFormData(prev => ({ ...prev, templateVariables: variables }))}
                        />
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                {/* Settings Card */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Einstellungen</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Visibility */}
                    <Form.Group className="mb-3">
                      <Form.Label>Sichtbarkeit</Form.Label>
                      <div className="d-flex gap-2 mb-2">
                        <Button
                          type="button"
                          variant={formData.visibility === 'public' ? 'success' : 'outline-success'}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, visibility: 'public' }))}
                        >
                          <Eye size={14} className="me-1" />
                          Öffentlich
                        </Button>
                        <Button
                          type="button"
                          variant={formData.visibility === 'team' ? 'info' : 'outline-info'}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, visibility: 'team' }))}
                        >
                          <Users size={14} className="me-1" />
                          Team-spezifisch
                        </Button>
                        <Button
                          type="button"
                          variant={formData.visibility === 'private' ? 'secondary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, visibility: 'private' }))}
                        >
                          <Lock size={14} className="me-1" />
                          Privat
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        {formData.visibility === 'public' && 'Jeder kann dieses Prompt ansehen und verwenden'}
                        {formData.visibility === 'team' && 'Nur Team-Mitglieder können auf dieses Prompt zugreifen'}
                        {formData.visibility === 'private' && 'Nur du kannst dieses Prompt sehen'}
                      </Form.Text>
                    </Form.Group>

                    {/* Category */}
                    <Form.Group className="mb-3">
                      <Form.Label>Kategorie</Form.Label>
                      <Form.Select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        {categoryOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {/* AI Models */}
                    <Form.Group className="mb-3">
                      <Form.Label>Kompatible KI-Modelle</Form.Label>
                      <div className="d-flex flex-wrap gap-1">
                        {aiModelOptions.map(model => (
                          <Button
                            key={model.value}
                            type="button"
                            variant={formData.aiModels.includes(model.value) ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleAIModelChange(model.value)}
                          >
                            {model.label}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    {/* Template Toggle */}
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="isTemplate"
                        checked={formData.isTemplate}
                        onChange={handleInputChange}
                        label="Als Template markieren"
                      />
                      <Form.Text className="text-muted">
                        Templates können mit verschiedenen Variablen wiederverwendet werden
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Tags Card */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Schlagwörter</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Selected Tags */}
                    {selectedTags.length > 0 && (
                      <div className="mb-3">
                        <h6 className="small text-muted mb-2">Ausgewählte Schlagwörter</h6>
                        <div className="d-flex flex-wrap gap-1">
                          {selectedTags.map(tag => (
                            <Badge
                              key={tag.id}
                              bg="primary"
                              className="d-flex align-items-center gap-1"
                            >
                              {tag.displayName}
                              <X
                                size={12}
                                className="cursor-pointer"
                                onClick={() => handleTagRemove(tag.id)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add New Tag */}
                    <Form.Group className="mb-3">
                      <Form.Label>Neues Schlagwort hinzufügen</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          size="sm"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder="Gib den Namen des neuen Schlagworts ein"
                          onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                        />
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={handleCreateTag}
                          disabled={!newTag.trim() || createTagMutation.isLoading}
                        >
                          <Plus size={14} />
                        </Button>
                      </div>
                    </Form.Group>

                    {/* Popular Tags */}
                    {popularTags?.data && popularTags.data.length > 0 && (
                      <div>
                        <h6 className="small text-muted mb-2">Beliebte Schlagwörter</h6>
                        <div className="d-flex flex-wrap gap-1">
                          {popularTags.data.map(tag => (
                            <Button
                              key={tag.id}
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleTagSelect(tag)}
                              disabled={selectedTags.find(t => t.id === tag.id)}
                            >
                              <Tag size={12} className="me-1" />
                              {tag.displayName}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Tags */}
                    {tagsData?.data && tagsData.data.length > 0 && (
                      <div className="mt-3">
                        <h6 className="small text-muted mb-2">Alle Schlagwörter</h6>
                        <div className="d-flex flex-wrap gap-1">
                          {tagsData.data.slice(0, 10).map(tag => (
                            <Button
                              key={tag.id}
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleTagSelect(tag)}
                              disabled={selectedTags.find(t => t.id === tag.id)}
                            >
                              {tag.displayName}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Preview Card */}
                <Card className="mb-4">
                  <PromptPreview 
                    prompt={{
                      ...formData,
                      tags: selectedTags
                    }}
                  />
                </Card>

                {/* Submit Button */}
                <Card>
                  <Card.Body>
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100"
                      disabled={createPromptMutation.isLoading}
                    >
                      {createPromptMutation.isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Erstelle...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="me-2" />
                          Prompt erstellen
                        </>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default CreatePromptPage;
